import { GameScene, CLIENT_OPTIONS_STORAGE_KEY, type SavedClientOptions } from '../GameScene';
import { AVAILABLE_INVENTORY_FONTS, AVAILABLE_PLAYER_NAMES_FONTS } from '@shared/constants';

/**
 * Persistencia de las opciones del cliente en localStorage: carga (loadClientOptions), guarda todo el
 * blob de opciones (saveClientOptions), y guarda posiciones de paneles/consola sin pisar el resto
 * (savePanelPosition/saveConsolePosition). Extraído de GameScene (god-object). Accede al scene vía
 * `this.scene.*`.
 */
export class PersistenceController {
  constructor(private scene: GameScene) {}

  public loadClientOptions(): Partial<SavedClientOptions> | null {
    if (typeof window === 'undefined') return null;

    try {
      const raw = window.localStorage.getItem(CLIENT_OPTIONS_STORAGE_KEY);

      if (!raw) return null;

      const parsed = JSON.parse(raw) as Partial<SavedClientOptions>;

      if (typeof parsed.showGrid === 'boolean') {
        this.scene.showGrid = parsed.showGrid;
      }

      if (typeof parsed.showGridCoords === 'boolean') {
        this.scene.showGridCoords = parsed.showGridCoords;
      }

      if (parsed.npcNameVisibilityMode === 'always' || parsed.npcNameVisibilityMode === 'hover') {
        this.scene.npcNameVisibilityMode = parsed.npcNameVisibilityMode;
      }

      if (
        parsed.hostileNpcNameVisibilityMode === 'always' ||
        parsed.hostileNpcNameVisibilityMode === 'hover'
      ) {
        this.scene.hostileNpcNameVisibilityMode = parsed.hostileNpcNameVisibilityMode;
      }

      if (typeof parsed.showBorderBlocks === 'boolean') {
        this.scene.showBorderBlocks = parsed.showBorderBlocks;
      }

      if (typeof parsed.showFloorBlocks === 'boolean') {
        this.scene.showFloorBlocks = parsed.showFloorBlocks;
      }

      if (typeof parsed.showPlayerTile === 'boolean') {
        this.scene.showPlayerTile = parsed.showPlayerTile;
      }

      if (typeof parsed.showHeadingArrow === 'boolean') {
        this.scene.showHeadingArrow = parsed.showHeadingArrow;
      }

      if (typeof parsed.hudScale === 'number') {
        // Migrar valores antiguos a los nuevos valores
        let scaleValue = parsed.hudScale;
        // Si es un valor antiguo (0.5, 1.5, 2.0, 2.5), convertirlo al nuevo sistema
        if (scaleValue === 0.5) {
          scaleValue = 1.0; // x0.5
        } else if (scaleValue === 1.5) {
          scaleValue = 2.0; // x1 (base)
        } else if (scaleValue === 2.0) {
          scaleValue = 2.0; // x1 (base) - se mantiene
        } else if (scaleValue === 2.5) {
          scaleValue = 3.0; // x1.5
        }
        this.scene.hud.hudScale = this.scene.getClosestHudScale(scaleValue);
      }

      // Cargar key bindings guardados
      if (parsed.keyBindings) {
        this.scene.inputManager?.applySavedBindings(parsed.keyBindings);
      }

      // Auto-retarget toggle (default true).
      if (typeof parsed.autoRetargetOnDeath === 'boolean') {
        this.scene.targets.autoRetargetOnDeath = parsed.autoRetargetOnDeath;
      }

      return parsed;
    } catch {
      // Ignorar errores de parseo / acceso a localStorage

      return null;
    }
  }

  public saveClientOptions(): void {
    if (typeof window === 'undefined') return;

    // Cargar opciones existentes para preservar posiciones de paneles

    let existing: Partial<SavedClientOptions> | null = null;

    try {
      const raw = window.localStorage.getItem(CLIENT_OPTIONS_STORAGE_KEY);

      if (raw) {
        existing = JSON.parse(raw) as Partial<SavedClientOptions>;
      }
    } catch {
      // Ignorar errores de parseo
    }

    const consoleState = this.scene.systemConsole?.getState();

    const data: SavedClientOptions = {
      showGrid: this.scene.showGrid,

      showGridCoords: this.scene.showGridCoords,

      npcNameVisibilityMode: this.scene.npcNameVisibilityMode,
      hostileNpcNameVisibilityMode: this.scene.hostileNpcNameVisibilityMode,

      showBorderBlocks: this.scene.showBorderBlocks,

      showFloorBlocks: this.scene.showFloorBlocks,

      showPlayerTile: this.scene.showPlayerTile,

      showHeadingArrow: this.scene.showHeadingArrow,

      hudScale: this.scene.hud.hudScale,

      // Fuentes seleccionadas
      uiFont: this.scene.getSelectedUIFont(),
      playerNamesFont: this.loadClientOptions()?.playerNamesFont ?? AVAILABLE_PLAYER_NAMES_FONTS[0].id,
      inventoryFont: this.loadClientOptions()?.inventoryFont ?? AVAILABLE_INVENTORY_FONTS[0].id,

      // Preservar posiciones de paneles si existen

      inventoryPanelX: existing?.inventoryPanelX,

      inventoryPanelY: existing?.inventoryPanelY,

      characterInfoPanelX: existing?.characterInfoPanelX,

      characterInfoPanelY: existing?.characterInfoPanelY,

      invokePanelX: existing?.invokePanelX,

      invokePanelY: existing?.invokePanelY,

      bodyGalleryPanelX: existing?.bodyGalleryPanelX,

      bodyGalleryPanelY: existing?.bodyGalleryPanelY,

      weaponGalleryPanelX: existing?.weaponGalleryPanelX,

      weaponGalleryPanelY: existing?.weaponGalleryPanelY,

      charFramePanelX: existing?.charFramePanelX,

      charFramePanelY: existing?.charFramePanelY,

      // Preservar posición y tamaño de consola si existen

      systemConsoleX: consoleState?.x ?? existing?.systemConsoleX,

      systemConsoleY: consoleState?.y ?? existing?.systemConsoleY,

      systemConsoleWidth: consoleState?.width ?? existing?.systemConsoleWidth,
      systemConsoleHeight: consoleState?.height ?? existing?.systemConsoleHeight,

      // Target panel (target lock system)
      targetPanelX: this.scene.targets.targetPanel?.getState().x ?? existing?.targetPanelX,
      targetPanelY: this.scene.targets.targetPanel?.getState().y ?? existing?.targetPanelY,
      targetPanelWidth: this.scene.targets.targetPanel?.getState().width ?? existing?.targetPanelWidth,
      targetPanelHeight: this.scene.targets.targetPanel?.getState().height ?? existing?.targetPanelHeight,
      autoRetargetOnDeath: this.scene.targets.autoRetargetOnDeath,

      keyBindings: this.scene.getKeyBindingsForStorage(),

      // Config de audio: usar la viva si el motor ya está inicializado; si no, preservar la guardada
      // (para no pisar los ajustes con los defaults antes de que el usuario desbloquee el audio).
      audio: this.scene.audio.isReady
        ? (this.scene.audio.getConfig() as SavedClientOptions['audio'])
        : existing?.audio,
    };

    try {
      window.localStorage.setItem(CLIENT_OPTIONS_STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Ignorar errores de almacenamiento
    }
  }

  public savePanelPosition(
    panelType: 'inventory' | 'invoke' | 'anim' | 'item' | 'bodyGallery' | 'weaponGallery' | 'charFrame' | 'characterInfo' | 'npcInfo' | 'merchant' | 'exchange' | 'marketplace' | 'spellVendor' | 'particle',
    x: number,
    y: number
  ): void {
    if (typeof window === 'undefined') return;

    // Cargar opciones existentes

    let existing: Partial<SavedClientOptions> | null = null;

    try {
      const raw = window.localStorage.getItem(CLIENT_OPTIONS_STORAGE_KEY);

      if (raw) {
        existing = JSON.parse(raw) as Partial<SavedClientOptions>;
      }
    } catch {
      // Ignorar errores de parseo
    }

    // Actualizar posición del panel específico

    const data: SavedClientOptions = {
      showGrid: this.scene.showGrid,

      showGridCoords: this.scene.showGridCoords,

      npcNameVisibilityMode: this.scene.npcNameVisibilityMode,
      hostileNpcNameVisibilityMode: this.scene.hostileNpcNameVisibilityMode,

      showBorderBlocks: this.scene.showBorderBlocks,

      showFloorBlocks: this.scene.showFloorBlocks,

      showPlayerTile: this.scene.showPlayerTile,

      // Preservar config de audio (estos saves no la tocan).
      audio: existing?.audio,

      // Preservar posiciones existentes

      inventoryPanelX: existing?.inventoryPanelX,

      inventoryPanelY: existing?.inventoryPanelY,

      characterInfoPanelX: existing?.characterInfoPanelX,

      characterInfoPanelY: existing?.characterInfoPanelY,

      invokePanelX: existing?.invokePanelX,

      invokePanelY: existing?.invokePanelY,

      bodyGalleryPanelX: existing?.bodyGalleryPanelX,

      bodyGalleryPanelY: existing?.bodyGalleryPanelY,

      weaponGalleryPanelX: existing?.weaponGalleryPanelX,

      weaponGalleryPanelY: existing?.weaponGalleryPanelY,

      charFramePanelX: existing?.charFramePanelX,

      charFramePanelY: existing?.charFramePanelY,
    };

    // Actualizar la posición del panel que se está moviendo
    if (panelType === 'inventory') {
      data.inventoryPanelX = x;
      data.inventoryPanelY = y;
    } else if (panelType === 'characterInfo') {
      data.characterInfoPanelX = x;
      data.characterInfoPanelY = y;
    } else if (panelType === 'invoke') {
      data.invokePanelX = x;
      data.invokePanelY = y;
    } else if (panelType === 'anim') {
      data.animPanelX = x;
      data.animPanelY = y;
    } else if (panelType === 'item') {
      data.itemPanelX = x;
      data.itemPanelY = y;
    } else if (panelType === 'bodyGallery') {
      data.bodyGalleryPanelX = x;
      data.bodyGalleryPanelY = y;
    } else if (panelType === 'weaponGallery') {
      data.weaponGalleryPanelX = x;
      data.weaponGalleryPanelY = y;
    } else if (panelType === 'charFrame') {
      data.charFramePanelX = x;
      data.charFramePanelY = y;
    } else if (panelType === 'merchant') {
      (data as any).merchantPanelX = x;
      (data as any).merchantPanelY = y;
    } else if (panelType === 'exchange') {
      (data as any).exchangePanelX = x;
      (data as any).exchangePanelY = y;
    } else if (panelType === 'marketplace') {
      (data as any).marketplacePanelX = x;
      (data as any).marketplacePanelY = y;
    } else if (panelType === 'spellVendor') {
      (data as any).spellVendorPanelX = x;
      (data as any).spellVendorPanelY = y;
    } else if (panelType === 'npcInfo') {
      (data as any).npcInfoPanelX = x;
      (data as any).npcInfoPanelY = y;
    } else if (panelType === 'particle') {
      data.particlePanelX = x;
      data.particlePanelY = y;
    }

    try {
      window.localStorage.setItem(CLIENT_OPTIONS_STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Ignorar errores de almacenamiento
    }
  }

  public saveConsolePosition(x: number, y: number, width: number, height: number): void {
    if (typeof window === 'undefined') return;

    // Cargar opciones existentes

    let existing: Partial<SavedClientOptions> | null = null;

    try {
      const raw = window.localStorage.getItem(CLIENT_OPTIONS_STORAGE_KEY);

      if (raw) {
        existing = JSON.parse(raw) as Partial<SavedClientOptions>;
      }
    } catch {
      // Ignorar errores de parseo
    }

    // Actualizar posición y tamaño de la consola

    const data: SavedClientOptions = {
      showGrid: this.scene.showGrid,

      showGridCoords: this.scene.showGridCoords,

      npcNameVisibilityMode: this.scene.npcNameVisibilityMode,
      hostileNpcNameVisibilityMode: this.scene.hostileNpcNameVisibilityMode,

      showBorderBlocks: this.scene.showBorderBlocks,

      showFloorBlocks: this.scene.showFloorBlocks,

      showPlayerTile: this.scene.showPlayerTile,

      // Preservar config de audio (estos saves no la tocan).
      audio: existing?.audio,

      // Preservar posiciones existentes

      inventoryPanelX: existing?.inventoryPanelX,

      inventoryPanelY: existing?.inventoryPanelY,

      characterInfoPanelX: existing?.characterInfoPanelX,

      characterInfoPanelY: existing?.characterInfoPanelY,

      invokePanelX: existing?.invokePanelX,

      invokePanelY: existing?.invokePanelY,

      // Actualizar posición y tamaño de consola

      systemConsoleX: x,

      systemConsoleY: y,

      systemConsoleWidth: width,

      systemConsoleHeight: height,
    };

    try {
      window.localStorage.setItem(CLIENT_OPTIONS_STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Ignorar errores de almacenamiento
    }
  }

}
